import { Extension, getText, getTextSerializersFromSchema, Range } from '@tiptap/core'
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'
import { Editor } from '@tiptap/core'
import { MatchesRepository } from '../model/languagetool/MatchesRepository'
import { EditorMatch, MatchesEntity } from '../model/languagetool/types'
import { TextMapper } from '../prosemirror/TextMapper'
import { StepMap } from '@tiptap/pm/transform'
import { Node, Schema } from '@tiptap/pm/model'
import { Segment, SizeChange } from '../config/types'
import { SentenceRepository } from '../model/paraphrase/SentenceRepository'
import { Events } from '../utils/events'
import EventEmitter from 'eventemitter3'

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        softcatala: {
            showSegments: (force: boolean) => ReturnType
        }
    }
}

type ExtensionOptions = {
    matchesRepository: MatchesRepository
    sentenceRepository: SentenceRepository
    eventEmitter: EventEmitter
}

type SoftcatalaExtensionStorage = {
    currentMatch : EditorMatch
}

type LoggedMatch = {
    time: number
    match: Segment | null
}

export const REFRESH_MATCHES = 'sc-showsegments'
export const FORCE_REFRESH_MATCHES = 'sc-force-showsegments'
const DOC_SCHEMA = 'document-schema'
export const SKIP_PROCESSING = "skip-processing-offsets"
const LAST_CLICK_TIMESTAMP = "update-click"

let globalEditor : Editor

export const SoftcatalaExtension = Extension.create<ExtensionOptions, SoftcatalaExtensionStorage>({
    name: 'softcatala',
    priority: 1000,

    addOptions() {
        return {
            matchesRepository: new MatchesRepository(),
            sentenceRepository: new SentenceRepository(),
            eventEmitter: new EventEmitter()
        }
    },

    addStorage() {
        return {
            currentMatch: new EditorMatch()
        }
    },

    addCommands() {
        return {
            showSegments: (force: boolean) => ({
                editor,
                tr,
            }) => {

                if (!globalEditor) {
                    globalEditor = editor
                }

                if (force) {
                    tr.setMeta(FORCE_REFRESH_MATCHES, true)
                }
                tr.setMeta(REFRESH_MATCHES, true)
                tr.setMeta(DOC_SCHEMA, editor.schema)

                return true
            }
        }
    },

    addProseMirrorPlugins() {
        const matchesRepository : MatchesRepository = this.options.matchesRepository
        const sentenceRepository : SentenceRepository = this.options.sentenceRepository
        const storage = this.storage
        const eventEmitter = this.options.eventEmitter
        return [

            new Plugin({
                key: new PluginKey("scSegmentDecorations"),
                state: {
                    init: () => {
                        return SegmentState.create(matchesRepository, sentenceRepository)
                    },
                    apply(tr, prev) {
                        return prev.apply(tr);
                    }
                },
                view: () => ({
                    update: () => {
                      
                    },
                }),
                props: {
                    decorations(state) { return this.getState(state)?.decorations },
                    handleClickOn(view: EditorView,
                        _pos: number,
                        _node: Node,
                        _nodePos: number,
                        event: MouseEvent) {


                        if (!globalEditor) {
                            return
                        }

                        const dispatch = (key : string, value: LoggedMatch) => {
                            globalEditor.commands.command(({state, dispatch}) => {
                                dispatch!(state.tr.setMeta(key, value))
                                return true
                            })
                        }

                        const matchId = (event.target as HTMLSpanElement).getAttribute('data-match-id')?.trim()                   
                        if (!matchId) {
                            storage.currentMatch.clear()
                            dispatch(LAST_CLICK_TIMESTAMP, { time: Date.now(), match: null})
                            return
                        }

                        const match = matchesRepository.get(matchId) || sentenceRepository.get(matchId)
                        
                        if (!match) {                           
                            storage.currentMatch.clear()
                            dispatch(LAST_CLICK_TIMESTAMP, { time: Date.now(), match: null})
                            eventEmitter.emit<Events>('set-selected-match', null)
                            return true
                        } else {
                            const state = view.state
                            const schema = state.schema
                            const text = getText(state.doc, { textSerializers: { ...getTextSerializersFromSchema(schema) } })
                    
                            const textMapper = new TextMapper(state.doc, text, { schema: schema})
                            const range = textMapper.getRange(match.offset, match.length)
                            
                            storage.currentMatch.update(match, matchId, range!)
                            if (match.segmentType === "Paraphrase") {
                                console.log("sending match now...", storage.currentMatch)
                                eventEmitter.emit<Events>('set-text', storage.currentMatch)
                                eventEmitter.emit<Events>('set-selected-match', matchId)
                            }

                            dispatch( LAST_CLICK_TIMESTAMP, { time: Date.now(), match})
                            return true
                        }
                    },
                    handlePaste() {
                        if (!globalEditor) {
                            return
                        }

                        const dispatch = (key : string, value: LoggedMatch) => {
                            globalEditor.commands.command(({state, dispatch}) => {
                                dispatch!(state.tr.setMeta(key, value))
                                return true
                            })
                        }

                        matchesRepository.reset()
                        sentenceRepository.reset()
                        dispatch(LAST_CLICK_TIMESTAMP, { time: Date.now(), match: null})
                    }
                },
            })
        ]
    },
})

class SegmentState {
    
    private schema?: Schema
    
    private match? : MatchesEntity

    constructor(public decorations: DecorationSet, private matchesRepository: MatchesRepository, private sentencesRepository: SentenceRepository) { 
        this.match = undefined
    }
    
    getSchema() {
        return this.schema
    }
    
    apply(tr: Transaction) {

        if (!this.schema) {
            if (!tr.getMeta(DOC_SCHEMA)) {
                return this
            } else {
                this.schema = tr.getMeta(DOC_SCHEMA)
            }
        }

        let isNewMatch = false

        if (tr.getMeta(LAST_CLICK_TIMESTAMP)) {
            const match = tr.getMeta(LAST_CLICK_TIMESTAMP).match

            if (match !== this.match) {
                this.match = match
                isNewMatch = true
            }
        }

        if (!tr.docChanged && !isNewMatch && !tr.getMeta(FORCE_REFRESH_MATCHES)) {
            if (this.matchesRepository.isEnabled() && !this.matchesRepository.hasMatches()) {
                return this
            }

            if (this.sentencesRepository.isEnabled() && !this.sentencesRepository.hasMatches()) {
                return this
            }

            if (!tr.getMeta(REFRESH_MATCHES)) {
                return this
            }
        }

        this.processChanges(tr)

        this.createDecorations(tr)

        return this
    }

    private createDecorations(tr: Transaction) {
        const text = this.getText(tr.doc)
        const textMapper = new TextMapper(tr.doc, text, { schema: this.schema })

        const segments : Segment[] = this.matchesRepository.isEnabled() 
            ? this.matchesRepository.getAll() 
            : this.sentencesRepository.getAll()

        const decorations: Decoration[] = []

        this.decorations = this.decorations.remove(this.decorations.find())

        if (!segments?.length) {
            return
        }

        for (const segment of segments) {

            const range = textMapper.getRange(segment.offset, segment.length)

            if (range) {
                decorations.push(decorate(range, segment))
            }
        }


        if (!decorations.length) {
            this.decorations = DecorationSet.empty
            return
        }

        this.decorations = this.decorations.add(tr.doc, decorations)
    }

    processChanges(tr: Transaction) {

        if (!tr.docChanged || tr.getMeta(SKIP_PROCESSING) || tr.getMeta(LAST_CLICK_TIMESTAMP)) {
            return
        }

        const oldText = this.getText(tr.before)
        const newText = this.getText(tr.doc)

        if (oldText === newText) {
            return
        }

        const mappingFrom = tr.mapping.from
        const mappingTo = tr.mapping.to

        const changes : SizeChange[]= []

        const textMapper = new TextMapper(tr.before, oldText, { schema: this.schema })

        for (let i = mappingFrom; i < mappingTo; i++) {
            const mapping: StepMap = tr.mapping.maps[i]

            mapping.forEach((oldStart, oldEnd, newStart, newEnd) => {
                if (oldEnd - oldStart !== newEnd - newStart) {
                    const textPosition = textMapper.getTextOffset(oldStart)
                    if (textPosition > -1) {
                        changes.push({
                            position: textPosition,
                            difference: newEnd - oldEnd
                        })
                    }
                }
            })
        }

        if (changes.length) {
            if (changes.length > 1) {
                console.warn("There should be only one change", changes)
            }
            this.matchesRepository.processChanges(changes[0])
            this.sentencesRepository.processChanges(changes[0])
        }
    }

    public getText(doc: Node) {
        if (!this.schema) {
            return ""
        }
        return getText(doc, { textSerializers: { ...getTextSerializersFromSchema(this.schema) } })
    }

    static create(matchesRepository: MatchesRepository, sentenceRepository: SentenceRepository) {
        return new SegmentState(DecorationSet.empty, matchesRepository, sentenceRepository)
    }
}

const decorate = (range: Range, segment: Segment) => {
    if (segment.segmentType === "SpellCheck") {
        const match = segment as MatchesEntity
        return Decoration.inline(range.from, range.to, {
            class: `sc-sentence lt lt-${match.rule.issueType}`,
            nodeName: 'span',
            'data-match-id': match.uuid,
        })
    } else {
        return Decoration.inline(range.from, range.to, {
            class: `sc-sentence sc-paraphrase`,
            nodeName: 'span',
            'data-match-id': segment.uuid,
        })
    }
}
    
